import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { updateGoal } from '../../api/goals';
import { Goal } from '../../api/types/Goal';
import { VentureId } from '../../api/ventures/types/Venture';
import { Venture } from '../../api/ventures/types/Venture';
import { SnackMessage } from '../../components/common';
import GoalOverviewForm, {
  GoalOverviewFormValues,
} from '../../components/forms/goal-overview';
import { useGoalContext } from '../../contexts/goal-context';

/**
 * Finds venture name by ventureId from ventures array or returns existing value
 */
const getVentureName = (
  ventureId: VentureId,
  ventures: Venture[],
  existingVentureName?: string | null,
): string => {
  if (existingVentureName) {
    return existingVentureName;
  }
  return ventures.find((v) => v.id === ventureId)?.ventureName || '';
};

function GoalOverviewPage() {
  const { goal, setGoal, ventures } = useGoalContext();
  const { enqueueSnackbar } = useSnackbar();

  const [isUpdateGoalLoading, setUpdateGoalLoading] = useState(false);

  const ventureOptions = useMemo(
    () => ventures.map((fv) => ({ value: fv.id, label: fv.ventureName })),
    [ventures],
  );

  const handleUpdateGoal = useCallback(
    async (updGoalData: Goal) => {
      try {
        setUpdateGoalLoading(true);
        const updatedGoal = await updateGoal(updGoalData);

        // Preserve ventureName: prefer from updatedGoal, then current goal, then find from ventures
        const ventureName = getVentureName(
          updatedGoal.ventureId,
          ventures,
          updatedGoal.ventureName || goal?.ventureName,
        );

        setGoal({
          ...updatedGoal,
          ventureName,
        });
        setUpdateGoalLoading(false);
        enqueueSnackbar('The goal was successfully updated.', {
          variant: 'success',
        });
      } catch (error) {
        setUpdateGoalLoading(false);
        const axiosError = error as AxiosError<{ message?: string }>;
        const messageError = axiosError.response?.data?.message;
        enqueueSnackbar(
          'An error occurred while updating the goal. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
      }
    },
    [enqueueSnackbar, setGoal, goal, ventures],
  );

  const handleSubmit = useCallback(
    async (values: GoalOverviewFormValues) => {
      if (!goal) {
        return;
      }

      const ventureName = getVentureName(
        values.ventureId,
        ventures,
        values.ventureName || goal.ventureName,
      );

      const goalData: Goal = {
        ...goal,
        title: values.title || '',
        ventureId: values.ventureId,
        ventureName,
        startDate: values.startDate || null,
        targetDate: values.targetDate || null,
        description: values.description || '',
      };

      await handleUpdateGoal(goalData);
    },
    [goal, handleUpdateGoal, ventures],
  );

  return (
    <GoalOverviewForm
      initialData={goal}
      loading={isUpdateGoalLoading}
      handleSubmit={handleSubmit}
      ventureOptions={ventureOptions}
    />
  );
}

export default GoalOverviewPage;
